import { useCallback, useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { fetcher } from 'repositories/BerthApi/berthApiClient'
import { getBookingMasterDataEndpoint } from 'repositories/BerthApi/getBookingMasterData/endpoint'
import type {
  BookingMasterData,
  GetBookingMasterDataResponse,
} from 'repositories/BerthApi/getBookingMasterData/type'
import { getTenantSettingsEndpoint } from 'repositories/BerthApi/getTenantSettings/endpoint'
import type { GetTenantSettingsResponse } from 'repositories/BerthApi/getTenantSettings/type'
import { getTenantShipment } from 'repositories/ShipmentApi/getTenantShipment'
import type { TenantShipmentDetail } from 'repositories/ShipmentApi/getTenantShipment/type'
import { downloadTenantShipmentAttachment } from 'repositories/ShipmentApi/downloadTenantShipmentAttachment'
import { TruckbookApiResponseError } from 'repositories/TruckbookApiResponseError'
import { bookingKeys, shipmentKeys, tenantKeys } from 'repositories/queryKeys'
import { useSnackbar } from 'components/Snackbar/useSnackbar'
import { isAuthError, isTenantSettingsLogoutError } from 'utils/apiErrorPredicates'
import { logout } from 'utils/logout'
import loginStorage from 'utils/loginStorage'

export type DeliveryPerformanceDetailControllerReturn = {
  shipment: TenantShipmentDetail | null
  masterData: BookingMasterData | null
  isLoading: boolean
  isError: boolean
  isFeatureOff: boolean
  handleBack: () => void
  handleDownloadAttachment: (visitId: number, attachmentId: number, name: string) => void
  snackbar: ReturnType<typeof useSnackbar>['snackbar']
}

export const useDeliveryPerformanceDetailController =
  (): DeliveryPerformanceDetailControllerReturn => {
    const history = useHistory()
    const { shipmentId } = useParams<{ shipmentId: string }>()
    const session = loginStorage.getWarehouseSession()
    const tenantId = typeof session?.tenantId === 'number' ? session.tenantId : null

    const shipmentIdNum = Number(shipmentId)

    // 区分 ON/OFF を事前に取得。OFF なら詳細リクエストを抑制し案内表示に切り替える（一覧と同方針）
    const {
      data: tenantSettingsResponse,
      isLoading: isTenantSettingsLoading,
      error: tenantSettingsError,
    } = useQuery<GetTenantSettingsResponse>({
      queryKey: tenantKeys.settingsByTenant(String(tenantId ?? '')),
      queryFn: () => fetcher(getTenantSettingsEndpoint(String(tenantId!))),
      enabled: tenantId != null,
    })
    const useDeliveryTenant = tenantSettingsResponse?.data?.useDeliveryTenant ?? null

    const {
      data,
      isInitialLoading,
      isError: isQueryError,
      error,
    } = useQuery({
      queryKey: shipmentKeys.detail(tenantId ?? 0, shipmentIdNum),
      queryFn: () => getTenantShipment(tenantId as number, shipmentIdNum),
      // tenantSettings が区分ON を確認してからリクエストする
      enabled: tenantId != null && !Number.isNaN(shipmentIdNum) && useDeliveryTenant === true,
    })

    // 401/403 (認証切れ・アクセス権限なし): 一覧と同様にログアウトする。
    // 権限を外された状態で詳細 URL へ直接遷移した場合に発生する。
    // tenantSettings は加えて 404 + code "917"/"2000"（TB-1882: 非所属/権限なし/テナント不存在/
    // ユーザー不存在）でもログアウトする。詳細は utils/apiErrorPredicates を参照。
    const isLogoutError = isAuthError(error) || isTenantSettingsLogoutError(tenantSettingsError)
    useEffect(() => {
      if (isLogoutError) logout()
    }, [isLogoutError])

    // 区分 OFF: テナント設定が取得済みで use_delivery_tenant=false のとき。
    // 422 フォールバック: 設定が ON でも BE が 422 を返した場合（競合等）にも対応する。
    const is422 =
      (error instanceof TruckbookApiResponseError && error.httpStatus === 422) ||
      (isAxiosError(error) && error.response?.status === 422)
    const isFeatureOff =
      (tenantId != null && !isTenantSettingsLoading && useDeliveryTenant === false) || is422

    // 404（該当 shipment なし）は専用 UI を出さず、shipment == null 経由で
    // DeliveryPerformanceDetailView の共通エラー表示（読み込みに失敗しました）に集約する。
    // そのため isError からは除外している（404 で二重に isError 扱いしないための除外）。
    const is404 = error instanceof TruckbookApiResponseError && error.httpStatus === 404
    const isError = isQueryError && !is404 && !isLogoutError && !is422

    const shipment = data?.data ?? null

    const { data: masterDataResponse } = useQuery<GetBookingMasterDataResponse>({
      queryKey: bookingKeys.masterDataByTenant(String(tenantId ?? '')),
      queryFn: () => fetcher(getBookingMasterDataEndpoint(String(tenantId!))),
      enabled: tenantId != null,
    })
    const masterData = masterDataResponse?.data ?? null

    const { snackbar, notify } = useSnackbar()
    const isDownloadingRef = useRef(false)

    const handleBack = useCallback(() => {
      // 一覧から遷移してきた場合は goBack で検索条件付きの一覧 URL に復帰する。
      // 直リンク / リロードで履歴が無い場合は通常の一覧へフォールバックする。
      const state = history.location.state as { fromList?: boolean } | undefined
      if (state?.fromList) {
        history.goBack()
      } else {
        history.push('/delivery-performances')
      }
    }, [history])

    // 添付ファイルをダウンロードする。
    // BE は Content-Disposition: attachment でバイナリを返すため Blob を取得し a[download] で保存する。
    // NOTE: Blob レスポンスは interceptor で正規化されず AxiosError のまま通るため、
    // 詳細取得の 401/403（→ logout()）とは異なり、DL API の 401/403 は toast 表示のみ（副次操作のため許容）。
    const handleDownloadAttachment = useCallback(
      async (visitId: number, attachmentId: number, name: string) => {
        if (tenantId == null || isDownloadingRef.current) return
        isDownloadingRef.current = true
        try {
          const blob = await downloadTenantShipmentAttachment({
            tenantId,
            shipmentId: shipmentIdNum,
            visitId,
            attachmentId,
          })
          const url = URL.createObjectURL(blob)
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = name
          document.body.appendChild(anchor)
          anchor.click()
          anchor.remove()
          // 即時 revoke するとダウンロードが始まらないブラウザがあるため遅延させる
          setTimeout(() => URL.revokeObjectURL(url), 1000)
        } catch {
          notify({ message: 'ファイルの取得に失敗しました。', variant: 'error' })
        } finally {
          isDownloadingRef.current = false
        }
      },
      [tenantId, shipmentIdNum, notify]
    )

    return {
      shipment,
      masterData,
      // 区分確定前（tenantSettings 取得中）も含めてローディング扱いにし、案内/詳細のフラッシュを防ぐ
      isLoading: tenantId != null && (isTenantSettingsLoading || isInitialLoading),
      isError,
      isFeatureOff,
      handleBack,
      handleDownloadAttachment,
      snackbar,
    }
  }
